/**
 * VariableAutocomplete Component
 *
 * Generic autocomplete input for selecting context variables.
 * Supports filtering by available entities and keyboard navigation.
 */

import React, { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/utils';
import type { ContextVariable, ContextCategory } from '../types/variables';
import {
  getVariablesForEntities,
  CATEGORY_INFO,
  filterVariables,
  groupVariablesByCategory,
} from '../utils/context-variables';
import { Icon } from '@/shared/icons'
export interface VariableAutocompleteProps {
  /** Current value (e.g., "{{lead.status}}") */
  value: string;
  /** Change handler */
  onChange: (value: string) => void;
  /** Available entities for filtering variables */
  availableEntities: ContextCategory[];
  /** Placeholder text */
  placeholder?: string;
  /** Disabled state */
  disabled?: boolean;
  /** Additional className */
  className?: string;
}

/**
 * VariableAutocomplete - Input with autocomplete for context variables
 */
export const VariableAutocomplete: React.FC<VariableAutocompleteProps> = ({
  value,
  onChange,
  availableEntities,
  placeholder = 'Selecione uma variável...',
  disabled = false,
  className,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });

  // Get all available variables for the given entities
  const allVariables = useMemo(
    () => getVariablesForEntities(availableEntities),
    [availableEntities]
  );

  // Filter variables based on search term
  const filteredVariables = useMemo(
    () => filterVariables(allVariables, searchTerm),
    [allVariables, searchTerm]
  );

  // Group filtered variables by category
  const groupedVariables = useMemo(
    () => groupVariablesByCategory(filteredVariables),
    [filteredVariables]
  );

  // Flat list for keyboard navigation
  const flatVariables = useMemo(() => {
    const result: ContextVariable[] = [];
    // Maintain order: contact, conversation, lead, ticket, event, ai, system.
    // `system` por último — nos nós de ação o sistema entra nos filtros (a
    // aba Filtros dos gatilhos não oferece `system` em `availableEntities`,
    // então esta lista some sozinha lá). `cancellation` (o desvio pela saída
    // "Cancelado") fecha a lista: só chega nos nós abaixo do vermelho.
    const orderedCategories: ContextCategory[] = ['contact', 'conversation', 'lead', 'ticket', 'event', 'ai', 'system', 'cancellation'];
    for (const category of orderedCategories) {
      if (groupedVariables[category]) {
        result.push(...groupedVariables[category]);
      }
    }
    return result;
  }, [groupedVariables]);

  // Find display label for current value (show label for known variables, raw value for custom)
  const displayLabel = useMemo(() => {
    if (!value) return '';
    const variable = allVariables.find(v => v.fullPath === value);
    return variable?.label || value;
  }, [value, allVariables]);

  const isKnownVariable = useMemo(() => {
    if (!value) return false;
    return allVariables.some(v => v.fullPath === value);
  }, [value, allVariables]);

  // Check if custom value option should be shown
  const showCustomOption = useMemo(() => {
    if (!searchTerm.trim()) return false;
    return !allVariables.some(v => v.fullPath === searchTerm.trim());
  }, [searchTerm, allVariables]);

  // Total selectable items (variables + custom option if shown)
  const totalItems = flatVariables.length + (showCustomOption ? 1 : 0);

  // Update dropdown position
  useEffect(() => {
    if (isOpen && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
        width: Math.max(rect.width, 320),
      });
    }
  }, [isOpen]);

  // Reset selected index when filtered variables change
  useEffect(() => {
    setSelectedIndex(0);
  }, [filteredVariables.length]);

  // Scroll selected item into view
  useEffect(() => {
    if (isOpen && dropdownRef.current) {
      const selectedElement = dropdownRef.current.querySelector('[data-selected="true"]');
      if (selectedElement) {
        selectedElement.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [selectedIndex, isOpen]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        containerRef.current &&
        !containerRef.current.contains(target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(target)
      ) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Handle variable selection
  const handleSelect = (variable: ContextVariable) => {
    onChange(variable.fullPath);
    setIsOpen(false);
    setSearchTerm('');
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
        e.preventDefault();
        setIsOpen(true);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) => Math.min(prev + 1, totalItems - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) => Math.max(prev - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (showCustomOption && selectedIndex === flatVariables.length) {
          onChange(searchTerm.trim());
          setIsOpen(false);
          setSearchTerm('');
        } else if (flatVariables[selectedIndex]) {
          handleSelect(flatVariables[selectedIndex]);
        } else if (showCustomOption) {
          onChange(searchTerm.trim());
          setIsOpen(false);
          setSearchTerm('');
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setSearchTerm('');
        break;
      case 'Tab':
        setIsOpen(false);
        setSearchTerm('');
        break;
    }
  };

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    if (!isOpen) {
      setIsOpen(true);
    }
  };

  // Handle container click
  const handleContainerClick = () => {
    if (disabled) return;
    setIsOpen(true);
    inputRef.current?.focus();
  };

  // Highlight matching text in label
  const highlightMatch = (text: string) => {
    if (!searchTerm) return text;

    const lowerText = text.toLowerCase();
    const lowerSearch = searchTerm.toLowerCase();
    const index = lowerText.indexOf(lowerSearch);

    if (index === -1) return text;

    return (
      <>
        {text.slice(0, index)}
        <span className="bg-yellow-200 text-yellow-900">
          {text.slice(index, index + searchTerm.length)}
        </span>
        {text.slice(index + searchTerm.length)}
      </>
    );
  };

  // Render dropdown content
  const renderDropdown = () => {
    if (!isOpen) return null;

    const orderedCategories: ContextCategory[] = ['contact', 'conversation', 'lead', 'ticket', 'event', 'ai', 'system', 'cancellation'];
    let currentIndex = 0;

    return createPortal(
      <div
        ref={dropdownRef}
        className="fixed z-[9999] bg-white rounded-lg shadow-lg border border-gray-200 max-h-64 overflow-y-auto"
        style={{
          top: dropdownPosition.top,
          left: dropdownPosition.left,
          width: dropdownPosition.width,
        }}
      >
        {/* Search input inside dropdown */}
        <div className="sticky top-0 bg-white border-b border-gray-100 p-2">
          <input
            ref={inputRef}
            type="text"
            value={searchTerm}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder="Buscar variável..."
            className="w-full h-[38px] px-3 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
            autoFocus
          />
        </div>

        {/* Variables grouped by category */}
        {flatVariables.length > 0 ? (
          <div className="py-1">
            {orderedCategories.map((category) => {
              const categoryVariables = groupedVariables[category];
              if (!categoryVariables || categoryVariables.length === 0) return null;

              const categoryInfo = CATEGORY_INFO[category];

              return (
                <div key={category}>
                  {/* Category header */}
                  <div className="px-3 py-1.5 bg-gray-50 border-y border-gray-100">
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      {categoryInfo.icon} {categoryInfo.label}
                    </span>
                  </div>

                  {/* Variables in category */}
                  {categoryVariables.map((variable) => {
                    const isSelected = currentIndex === selectedIndex;
                    const itemIndex = currentIndex;
                    currentIndex++;

                    return (
                      <button
                        key={variable.value}
                        type="button"
                        data-selected={isSelected}
                        className={cn(
                          'w-full px-3 py-2 text-left flex items-center justify-between gap-2 hover:bg-gray-100 transition-colors',
                          isSelected && 'bg-violet-50'
                        )}
                        onClick={() => handleSelect(variable)}
                        onMouseEnter={() => setSelectedIndex(itemIndex)}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-gray-900 truncate">
                            {highlightMatch(variable.label)}
                            {variable.description && (
                              <span className="ml-1 text-xs text-gray-400">
                                ({variable.description})
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-gray-500 font-mono truncate">
                            {highlightMatch(variable.fullPath)}
                          </div>
                        </div>
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 flex-shrink-0">
                          {variable.type}
                        </span>
                      </button>
                    );
                  })}
                </div>
              );
            })}

            {/* Custom value option when results also exist */}
            {showCustomOption && (
              <div className="border-t border-gray-100">
                <button
                  type="button"
                  data-selected={selectedIndex === flatVariables.length}
                  className={cn(
                    'w-full px-3 py-2 text-left flex items-center gap-2 hover:bg-gray-100 transition-colors',
                    selectedIndex === flatVariables.length && 'bg-violet-50'
                  )}
                  onClick={() => {
                    onChange(searchTerm.trim());
                    setIsOpen(false);
                    setSearchTerm('');
                  }}
                  onMouseEnter={() => setSelectedIndex(flatVariables.length)}
                >
                  <span className="text-sm text-violet-600 flex-shrink-0">Usar:</span>
                  <span className="text-sm text-gray-900 font-mono truncate">
                    {searchTerm.trim()}
                  </span>
                </button>
              </div>
            )}
          </div>
        ) : showCustomOption ? (
          <div className="py-1">
            <button
              type="button"
              data-selected={selectedIndex === 0}
              className={cn(
                'w-full px-3 py-2 text-left flex items-center gap-2 hover:bg-gray-100 transition-colors',
                selectedIndex === 0 && 'bg-violet-50'
              )}
              onClick={() => {
                onChange(searchTerm.trim());
                setIsOpen(false);
                setSearchTerm('');
              }}
              onMouseEnter={() => setSelectedIndex(0)}
            >
              <span className="text-sm text-violet-600 flex-shrink-0">Usar:</span>
              <span className="text-sm text-gray-900 font-mono truncate">
                {searchTerm.trim()}
              </span>
            </button>
          </div>
        ) : (
          <div className="px-3 py-6 text-center text-sm text-gray-500">
            Nenhuma variável encontrada
          </div>
        )}

        {/* Help footer */}
        <div className="sticky bottom-0 px-3 py-1.5 bg-gray-50 border-t text-[10px] text-gray-400 flex justify-between">
          <span>↑↓ navegar</span>
          <span>Enter selecionar</span>
          <span>Esc fechar</span>
        </div>
      </div>,
      document.body
    );
  };

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      {/* Main input container */}
      <div
        onClick={handleContainerClick}
        onKeyDown={handleKeyDown}
        tabIndex={disabled ? -1 : 0}
        className={cn(
          'w-full h-[38px] px-3 py-2 border rounded-lg cursor-pointer flex items-center justify-between gap-2',
          'focus:outline-none focus:ring-2 focus:ring-violet-500',
          disabled && 'cursor-not-allowed opacity-60',
          isOpen && 'ring-2 ring-violet-500'
        )}
        style={{
          backgroundColor: disabled ? 'var(--theme-button-ghost-hover)' : 'var(--theme-input-background)',
          borderColor: isOpen ? '#8b5cf6' : 'var(--theme-input-border)',
          color: 'var(--theme-input-text)',
        }}
      >
        <span
          className={cn(
            'text-sm truncate',
            value ? (isKnownVariable ? '' : 'font-mono') : ''
          )}
          style={{
            color: value ? 'var(--theme-card-text)' : 'var(--theme-muted-text)',
          }}
        >
          {displayLabel || placeholder}
        </span>
        <Icon name="chevron-down" className={cn(
            'w-4 h-4 flex-shrink-0 transition-transform',
            isOpen && 'rotate-180'
          )}
          style={{ color: 'var(--theme-muted-text)' }} />
      </div>

      {/* Dropdown */}
      {renderDropdown()}
    </div>
  );
};

VariableAutocomplete.displayName = 'VariableAutocomplete';

export default VariableAutocomplete;
